import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  Signal,
  TemplateRef,
  WritableSignal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatButtonModule } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { TranslateModule } from '@ngx-translate/core'
import { ItemsFacade } from '@galabid/gb-dashboard/item/data-access'
import { GbDashboardItemUiModule } from '@galabid/gb-dashboard/item/ui'
import {
  GbDashboardSharedFeatureModule,
  GbTableColumn,
  GbTableItem,
  ColumnTypeEnum,
  GbTableColumnTemplate,
  GridSwitchEnum,
} from '@galabid/gb-dashboard/shared/feature'
import { combineLatest, map, of, shareReplay, take } from 'rxjs'
import * as ItemsActions from 'libs/gb-dashboard/item/data-access/src/lib/+state/items.actions'

export interface GbPresenterItemFilterDialogData {
  hiddenItemUuids: string[]
}

export interface GbPresenterItemFilterDialogResult {
  hiddenItemUuids: string[]
}

@Component({
  selector: 'gb-presenter-item-filter-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    TranslateModule,
    GbDashboardSharedFeatureModule,
    GbDashboardItemUiModule,
  ],
  templateUrl: './gb-presenter-item-filter-dialog.component.html',
  styleUrl: './gb-presenter-item-filter-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterItemFilterDialogComponent {
  itemsFacade = inject(ItemsFacade)
  dialogRef = inject(MatDialogRef<GbPresenterItemFilterDialogComponent, GbPresenterItemFilterDialogResult>)
  destroyRef = inject(DestroyRef)

  data: GbPresenterItemFilterDialogData = inject(MAT_DIALOG_DATA)

  GridSwitchEnum = GridSwitchEnum
  tableView$ = of(GridSwitchEnum.Table)

  loaded$ = this.itemsFacade.loaded$
  count$ = this.itemsFacade.count$

  items$ = this.itemsFacade.allItems$.pipe(
    map((items) => (items || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  selectedItems: WritableSignal<string[]> = signal<string[]>([])
  selectedItemCount: Signal<number> = computed(() => this.selectedItems().length)
  hiddenItemUuids: WritableSignal<string[]> = signal<string[]>([])
  hiddenItemCount: Signal<number> = computed(() => this.hiddenItemUuids().length)

  tableData$ = this.items$.pipe(
    map((items): GbTableItem[] =>
      items.map((item) => ({
        uuid: item.uuid,
        name: item.name,
        alias: item.alias,
        item_type: item.item_type,
        bidding_item: item.bidding_item,
        is_visible: item.is_visible,
      })),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  displayedColumns$ = of<GbTableColumn[]>([
    {
      columnKey: 'name',
      columnLabel: 'NAME',
      columnType: ColumnTypeEnum.Label,
    },
    {
      columnKey: 'alias',
      columnLabel: 'ITEMS.IOC_SHOP.ALIAS',
      columnType: ColumnTypeEnum.Label,
      disableSort: true,
    },
    {
      columnKey: 'item_type',
      columnLabel: 'ITEMS.LIST.TYPE',
      columnType: ColumnTypeEnum.Template,
    },
    {
      columnKey: 'is_visible',
      columnLabel: 'PRESENTER.ITEM_TABLE_TOOLS.FILTER_ITEMS.CAMPAIGN_VISIBILITY',
      columnType: ColumnTypeEnum.Visibility,
      disableSort: true,
    },
  ])

  vm$ = combineLatest([this.items$, this.loaded$, this.count$]).pipe(
    map(([items, loaded, count]) => ({ items, loaded, count })),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  ngOnInit(): void {
    this.hiddenItemUuids.set([...(this.data.hiddenItemUuids || [])])

    this.items$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((items) => {
      const allUuids = items.map((i) => i.uuid)
      const selected = allUuids.filter((uuid) => !this.hiddenItemUuids().includes(uuid))
      this.selectedItems.set(selected)
    })

    this.count$.pipe(take(1)).subscribe((count) => {
      if (count === 0) {
        this.itemsFacade.dispatch(ItemsActions.loadItems({ projector: true, limit: 20 }))
      }
    })
  }

  loadMoreItems() {
    this.itemsFacade.dispatch(ItemsActions.loadItems({ projector: true, limit: 20 }))
  }

  getDisplayedColumns$(itemType: TemplateRef<GbTableColumnTemplate>) {
    return this.displayedColumns$.pipe(
      map((columns) =>
        columns.map((column) => (column.columnKey === 'item_type' ? { ...column, columnTemplate: itemType } : column)),
      ),
    )
  }

  onSelectedItemsChange(selectedItems: string[]): void {
    this.selectedItems.set([...selectedItems])

    this.items$.pipe(take(1)).subscribe((items) => {
      const currentlyLoadedUuids = items.map((i) => i.uuid)

      const newlyHidden = currentlyLoadedUuids.filter((uuid) => !selectedItems.includes(uuid))

      const newlyShown = selectedItems

      const updatedHidden = new Set(this.hiddenItemUuids())
      newlyHidden.forEach((uuid) => updatedHidden.add(uuid))
      newlyShown.forEach((uuid) => updatedHidden.delete(uuid))

      this.hiddenItemUuids.set(Array.from(updatedHidden))
    })
  }

  cancel(): void {
    this.dialogRef.close()
  }

  apply(): void {
    this.dialogRef.close({ hiddenItemUuids: this.hiddenItemUuids() })
  }
}
